import { useEffect, useRef, useState } from "react";
import { isEmpty, kebabCase, startCase } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@hydra/atom/components";

import { BoxedContent } from "@/components/common";
import { DynamicFormContainer } from "@/components/dynamic";
import { getDynamicObjectRecordById } from "@/api/dynamic/dynamicObjectNameApi";

import { formatDecimalValues, getTestId } from "@/utils/helpers";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { useTaskRedirect } from "@/hooks";

const formatMaterialRequest = (materialRequest) => {
  const {
    itemDetail, itemType, costCenter, approverMap, status, currency, ...rest
  } = materialRequest;

  const data = {
    ...rest,
    approverMap: {
      label: approverMap.approverMapName,
      value: approverMap.id
    },
    currency: {
      label: currency.code,
      value: approverMap.id
    },
    status: {
      label: startCase(status),
      value: status,
    },
    itemType: {
      label: itemType,
      value: itemType,
    },
    costCenter: !isEmpty(costCenter) ?
      {
        lookupObjectName: dynamicObjectMap.get("CostCenterObjectName"),
        label: costCenter?.name,
        value: costCenter?.id,
      } :
      null,
    itemDetail: itemDetail?.map((lineItem) => {
      const {
        item,
        deliveryLocation,
        unitOfMeasure,
        supplier,
        type
      } = lineItem;

      return {
        ...lineItem,
        item: {
          label: item.itemName,
          value: item.id,
        },
        deliveryLocation: !isEmpty(deliveryLocation) ?
          {
            label: deliveryLocation.name,
            value: deliveryLocation.id,
          } :
          null,
        unitOfMeasure: !isEmpty(unitOfMeasure) ?
          {
            label: unitOfMeasure.name,
            value: unitOfMeasure.id,
          } :
          null,
        supplier: !isEmpty(supplier) ?
          {
            label: supplier.name,
            value: supplier.id,
          } :
          null,
        type: {
          label: type,
          value: type
        },
        converted: {
          label: "Pending",
          value: "Pending"
        },
        convertedQuantity: 0
      };
    }),
  };

  return data;
};

function MaterialRequestForm() {
  const navigate = useNavigate();
  const { redirect } = useTaskRedirect();
  const [isLoading, setIsLoading] = useState(false);
  const ref = useRef(null);
  const [searchParams] = useSearchParams();
  const materialRequest = searchParams.get("materialRequest");
  const [initialState, setInitialState] = useState({});
  const taskId = searchParams.get("taskId");

  const { data: materialRequestData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("MaterialRequestObjectName")),
      materialRequest,
    ],
    () =>
      getDynamicObjectRecordById(
        dynamicObjectMap.get("MaterialRequestObjectName"),
        materialRequest
      ),
    {
      enabled: Boolean(materialRequest),
    }
  );

  useEffect(() => {
    if (materialRequestData) {
      const formattedMaterialRequest =
        formatMaterialRequest(materialRequestData);
      setInitialState(formattedMaterialRequest);
    }
  }, [materialRequestData]);

  const updateItemDetail = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { costCenter, itemDetail } = formState;

    if (costCenter) {
      const updatedItemDetail = itemDetail.map((i) => ({
        costCenter,
        ...i,
      }));

      ref.current.setFormValue("itemDetail", updatedItemDetail);
    }
  };

  const onStateChange = (key, value) => {
    if (key === "itemType") {
      const formState = ref.current.getState();
      const { itemType: oldItemType, itemDetailTable } = formState;

      if (oldItemType?.value === value?.value) {
        return;
      }

      ref.current.setFormState({
        itemDetail: [],
        itemDetailTable: {
          ...itemDetailTable,
          type: value
        }
      });
    }

    if (key === "costCenter") {
      updateItemDetail(key, value);
    }
  };

  const onChildStateChange = ({
    index, key, value, parentField, parentFieldType
  }) => {
    const formState = ref.current.getState();
    const stateKey = `${parentField}${parentFieldType}`;
    let parentFieldState = formState[stateKey] ?? {};

    if (index > -1) {
      parentFieldState = formState[parentField][index];
    }

    if (parentField === "itemDetail") {
      switch (key) {
        case "item":
          parentFieldState[key] = value;
          if (value) {
            const { unitOfMeasure } = value;
            if (!isEmpty(unitOfMeasure)) {
              parentFieldState.unitOfMeasure = {
                label: unitOfMeasure.name,
                value: unitOfMeasure.id
              };
            }
          }
          break;
        case "originatingAmount":
        case "quantity": {
          parentFieldState[key] = value;
          const { quantity, originatingAmount } = parentFieldState;

          if (!quantity || !originatingAmount) {
            return;
          }

          parentFieldState.amount = originatingAmount;
          const totalAmount = formatDecimalValues(quantity * originatingAmount);
          parentFieldState.totalAmount = totalAmount;
          parentFieldState.totalOriginatingAmount = totalAmount;
          break;
        }
        default:
          break;
      }
    }
  };

  const submitForm = () => {
    const submitButton = document.getElementById(
      `dynamic-form-submit-button-${kebabCase(dynamicObjectMap.get("MaterialRequestObjectName"))}`
    );
    submitButton.click();
  };

  const handleSave = () => {
    const formState = ref.current.getState();
    formState.status = {
      label: "Draft",
      value: "Draft"
    };
    submitForm();
  };

  const handleSubmit = () => {
    const formState = ref.current.getState();
    formState.status = {
      label: "In Progress",
      value: "InProgress"
    };
    formState.creationDate = new Date();
    submitForm();
  };

  const handleSuccess = (recordId) => {
    redirect(-1, {
      recordId,
      success: true,
    });
  };

  return (
    <BoxedContent>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        objectName={dynamicObjectMap.get("MaterialRequestObjectName")}
        onStateChange={onStateChange}
        onChildStateChange={onChildStateChange}
        setIsLoading={(loading) => setIsLoading(loading)}
        showHeader
        readOnlyFields={materialRequest ? ["itemType", "currency"] : []}
        showButtons={false}
        onSuccess={handleSuccess}
        navigate={false}
      />
      <div className="buttons-container buttons-at-end">
        <Button
          small
          bordered
          onClick={() => navigate(-1)}
          testId={getTestId(`${dynamicObjectMap.get("MaterialRequestObjectName")}-Submit-Button`)}
        >
          Cancel
        </Button>
        {
          !taskId && (
            <Button
              small
              onClick={handleSave}
              loading={isLoading}
              testId={getTestId(`${dynamicObjectMap.get("MaterialRequestObjectName")}-Submit-Button`)}
            >
              Save
            </Button>
          )
        }

        {
          !taskId && (
            <Button
              small
              onClick={handleSubmit}
              loading={isLoading}
              testId={getTestId(`${dynamicObjectMap.get("MaterialRequestObjectName")}-Submit-Button`)}
            >
              Submit
            </Button>
          )
        }

        {
          taskId && (
            <Button
              small
              onClick={submitForm}
              loading={isLoading}
              testId={getTestId(`${dynamicObjectMap.get("MaterialRequestObjectName")}-Submit-Button`)}
            >
              Update
            </Button>
          )
        }
      </div>
    </BoxedContent>
  );
}

export default MaterialRequestForm;
